import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form, useNavigation, useSearchParams } from 'react-router';
import { z } from 'zod';

import CircleIcon from '@shieldpay/bumblebee/icons/CircleIcon';
import { UnitedKingdom } from '@shieldpay/bumblebee/images';

import { Button } from '@shieldpay/bumblebee/button';
import { CopyTextIcon } from '@shieldpay/bumblebee/copy-text-icon';
import { Drawer } from '@shieldpay/bumblebee/drawer';
import { Heading } from '@shieldpay/bumblebee/heading';
import { Infoblock } from '@shieldpay/bumblebee/infoblock';
import { InputField } from '@shieldpay/bumblebee/input-field';
import { Column, Row } from '@shieldpay/bumblebee/layout';
import { List } from '@shieldpay/bumblebee/list';
import DrawerMarkdown from '@shieldpay/bumblebee/markdown/modal.md';
import { Modal } from '@shieldpay/bumblebee/modal';
import { Pill } from '@shieldpay/bumblebee/pill';
import { Tag } from '@shieldpay/bumblebee/tag';
import { Text } from '@shieldpay/bumblebee/text';

import type { Route } from './+types/drawer';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { RandomText } from '~/components/random-text/random-text';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.drawer.title',
});

export const clientAction = async (_args: Route.ClientActionArgs) => {
  // artificial delay to show loading state
  // eslint-disable-next-line no-console
  console.log('[clientAction] waiting for 1 second');

  await new Promise((resolve) => setTimeout(resolve, 1000));

  // eslint-disable-next-line no-console
  console.log('[clientAction] return success');

  return { successAt: Date.now() };
};

const drawer1tabs = ['draw1tab1', 'draw1tab2', 'draw1tab3'] as const;
const drawer2tabs = ['draw2tab1', 'draw2tab2', 'draw2tab3'] as const;

const drawer1TabContent = {
  [drawer1tabs[0]]: (
    <Column spacing="base">
      <List>
        {Array.from({ length: 10 }, (_, index) => (
          <List.Item
            key={index}
            variant="timeline"
            LeftIcon={
              <CircleIcon color={index < 3 ? 'success500' : 'neutral250'} />
            }
            position="first"
          >
            <Text>
              <RandomText maxWords={5} />
            </Text>
            {index < 3 ? <Pill variant="success">Pass</Pill> : null}
            <Text color="neutral450">{20 - index} March 2023, 12:20</Text>
          </List.Item>
        ))}
      </List>
    </Column>
  ),
  [drawer1tabs[1]]: (
    <Column spacing="base">
      <RandomText paragraphCount={2} />
    </Column>
  ),
  [drawer1tabs[2]]: (
    <Column spacing="base">
      <RandomText paragraphCount={7} />
    </Column>
  ),
};

const drawer2TabContent = {
  [drawer2tabs[0]]: (
    <Column spacing="base">
      <InputField label="Field" id="field" name="field" />
      <RandomText paragraphCount={6} />
    </Column>
  ),
  [drawer2tabs[1]]: (
    <Column spacing="base">
      <RandomText paragraphCount={1} />
    </Column>
  ),
  [drawer2tabs[2]]: (
    <Column spacing="base">
      <RandomText paragraphCount={6} />
    </Column>
  ),
};

const StateSchema = z.object({
  drawer1Open: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  drawer1Tab: z.enum(drawer1tabs).default(drawer1tabs[0]),
  drawer2Open: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  drawer2Tab: z.enum(drawer2tabs).default(drawer2tabs[0]),
});

type Params = z.infer<typeof StateSchema>;

const useStateParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = StateSchema.parse(Object.fromEntries(searchParams.entries()));

  const updateSearchParams = (newParams: Partial<Params>) => {
    setSearchParams((prev) => {
      Object.entries(newParams).forEach(([key, value]) => {
        prev.set(key, value.toString());
      });
      return prev;
    });
  };

  return [params, updateSearchParams] as const;
};

const TagsAndPills = () => (
  <Drawer.Summary>
    <Drawer.DisplayField label=" A Pill">
      <Pill variant="default">overflowing text in a pill</Pill>
    </Drawer.DisplayField>

    <Drawer.DisplayField label=" A tag">
      <Tag variant="default">
        <UnitedKingdom size="xs" alt="" /> New
      </Tag>
    </Drawer.DisplayField>

    <Drawer.DisplayField label="Another">
      <Tag variant="default">New</Tag>
    </Drawer.DisplayField>

    <Drawer.DisplayField label="Copy Text">
      <CopyTextIcon
        variant="tag"
        copiedTooltipText="Text copied!"
        idleTooltipText="Copy text"
      >
        Copy Me
      </CopyTextIcon>
    </Drawer.DisplayField>
  </Drawer.Summary>
);

const DrawerComponentPage = ({ actionData }: Route.ComponentProps) => {
  const [params, updateSearchParams] = useStateParams();

  const [modalOpen, setModalOpen] = useState(false);

  const { state } = useNavigation();
  const successAt = actionData?.successAt;

  useEffect(() => {
    if (params.drawer2Open && successAt) {
      updateSearchParams({ drawer2Open: false });
    }
  }, [successAt, updateSearchParams, params]);

  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.drawer.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview>
        <Row spacing="basePos3">
          <Button
            onClick={() =>
              updateSearchParams({ drawer1Open: !params.drawer1Open })
            }
          >
            Open drawer
          </Button>

          <Button
            onClick={() =>
              updateSearchParams({ drawer2Open: !params.drawer2Open })
            }
          >
            Open a 'controlled' drawer which has a POST method
          </Button>

          <Drawer
            open={params.drawer1Open}
            onClose={() => updateSearchParams({ drawer1Open: false })}
            closeDialogText="close me"
          >
            <Drawer.Header>
              <Drawer.Title>Drawer title</Drawer.Title>
              <TagsAndPills />
              <Button onClick={() => setModalOpen(true)}>Open modal</Button>
              <Infoblock variant="warning">
                <RandomText />
              </Infoblock>
              <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                closeDialogText="close me"
              >
                <Modal.Actions>
                  <Modal.CloseButton>Close</Modal.CloseButton>
                </Modal.Actions>
              </Modal>
            </Drawer.Header>
            <Drawer.Tabs>
              <Drawer.TabsItem
                id={drawer1tabs[0]}
                onClick={() =>
                  updateSearchParams({ drawer1Tab: drawer1tabs[0] })
                }
                selectedTab={params.drawer1Tab}
              >
                Tab 1
              </Drawer.TabsItem>
              <Drawer.TabsItem
                id={drawer1tabs[1]}
                onClick={() =>
                  updateSearchParams({ drawer1Tab: drawer1tabs[1] })
                }
                selectedTab={params.drawer1Tab}
              >
                Tab 2
              </Drawer.TabsItem>
              <Drawer.TabsItem
                id={drawer1tabs[2]}
                onClick={() =>
                  updateSearchParams({ drawer1Tab: drawer1tabs[2] })
                }
                selectedTab={params.drawer1Tab}
              >
                Tab 3
              </Drawer.TabsItem>
            </Drawer.Tabs>

            <Drawer.Content>
              {drawer1TabContent[params.drawer1Tab]}
            </Drawer.Content>
            <Drawer.Actions>
              <Button>Secondary Action</Button>
              <Drawer.CloseButton>Primary Action</Drawer.CloseButton>
            </Drawer.Actions>
          </Drawer>

          <Drawer
            open={params.drawer2Open}
            onClose={() => updateSearchParams({ drawer2Open: false })}
            closeDialogText="close me"
            method="POST"
            Component={Form}
          >
            <Drawer.Header>
              <Drawer.Title>Drawer title</Drawer.Title>
              <TagsAndPills />
            </Drawer.Header>
            <Drawer.Tabs>
              <Drawer.TabsItem
                id={drawer2tabs[0]}
                onClick={() =>
                  updateSearchParams({ drawer2Tab: drawer2tabs[0] })
                }
                selectedTab={params.drawer2Tab}
              >
                Tab 1
              </Drawer.TabsItem>
              <Drawer.TabsItem
                id={drawer2tabs[1]}
                onClick={() =>
                  updateSearchParams({ drawer2Tab: drawer2tabs[1] })
                }
                selectedTab={params.drawer2Tab}
              >
                Tab 2
              </Drawer.TabsItem>
              <Drawer.TabsItem
                id={drawer2tabs[2]}
                onClick={() =>
                  updateSearchParams({ drawer2Tab: drawer2tabs[2] })
                }
                selectedTab={params.drawer2Tab}
              >
                Tab 3
              </Drawer.TabsItem>
            </Drawer.Tabs>

            <Drawer.Content>
              {drawer2TabContent[params.drawer2Tab]}
            </Drawer.Content>
            <Drawer.Actions>
              <Drawer.CloseButton>Close</Drawer.CloseButton>
              <Button type="submit" loading={state === 'submitting'}>
                Submit
              </Button>
            </Drawer.Actions>
          </Drawer>
        </Row>
      </ComponentPreview>

      <MdxFormatter md={DrawerMarkdown} />
    </>
  );
};

export default DrawerComponentPage;
